import { useState, useEffect, useMemo } from 'react'

const METRICS = [
  { label: 'AI Accuracy', color: '#fbbf24' },
  { label: 'Speed', color: '#10b981' },
  { label: 'Profitability', color: '#f59e0b' },
  { label: 'Liquidity', color: '#8b5cf6' },
  { label: 'Security', color: '#3b82f6' },
  { label: 'Uptime', color: '#10b981' },
]

const CX = 150
const CY = 150
const R = 110
const LEVELS = 4

function polarToXY(angle: number, radius: number): [number, number] {
  const rad = (angle - 90) * (Math.PI / 180)
  return [CX + radius * Math.cos(rad), CY + radius * Math.sin(rad)]
}

function makePolygon(count: number, radiusFn: (i: number) => number): string {
  const step = 360 / count
  return Array.from({ length: count }, (_, i) => {
    const [x, y] = polarToXY(i * step, radiusFn(i))
    return `${x},${y}`
  }).join(' ')
}

export function RadarBlock() {
  const n = METRICS.length
  const step = 360 / n

  const [values, setValues] = useState(() => METRICS.map(() => 0.90 + Math.random() * 0.09))

  useEffect(() => {
    const id = setInterval(() => {
      setValues(prev =>
        prev.map(v => {
          const delta = (Math.random() - 0.5) * 0.04
          return Math.min(0.99, Math.max(0.90, v + delta))
        }),
      )
    }, 800)
    return () => clearInterval(id)
  }, [])

  const dataPoints = useMemo(
    () => makePolygon(n, i => values[i] * R),
    [values, n],
  )

  return (
    <div className="card p-3 sm:p-5 flex flex-col h-full min-h-[300px] sm:min-h-[260px] overflow-hidden">
      <h2 className="text-sm sm:text-base font-extrabold text-white">System Reliability</h2>

      <div className="flex-1 flex items-center justify-center min-h-0 mt-1">
        <svg viewBox="0 0 300 300" className="w-full max-w-[280px] max-h-full">
          {/* Grid levels */}
          {Array.from({ length: LEVELS }, (_, l) => {
            const r = (R / LEVELS) * (l + 1)
            return (
              <polygon
                key={l}
                points={makePolygon(n, () => r)}
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth={1}
              />
            )
          })}

          {/* Axis lines */}
          {METRICS.map((_, i) => {
            const [x, y] = polarToXY(i * step, R)
            return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
          })}

          {/* Data polygon */}
          <polygon
            points={dataPoints}
            fill="rgba(251, 191, 36, 0.12)"
            stroke="#fbbf24"
            strokeWidth={2}
            strokeLinejoin="round"
            style={{ transition: 'all 1s ease' }}
          />

          {/* Data dots + labels */}
          {METRICS.map((m, i) => {
            const [dx, dy] = polarToXY(i * step, values[i] * R)
            const [lx, ly] = polarToXY(i * step, R + 22)
            return (
              <g key={m.label}>
                <circle cx={dx} cy={dy} r={4} fill={m.color} style={{ transition: 'all 1s ease' }} />
                <circle cx={dx} cy={dy} r={7} fill={m.color} opacity={0.2} style={{ transition: 'all 1s ease' }} />
                <text
                  x={lx}
                  y={ly}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-gray-400"
                  style={{ fontSize: '11px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}
                >
                  {m.label}
                </text>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Legend row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 mt-2 pt-2 border-t border-white/[0.06] shrink-0">
        {METRICS.map((m, i) => (
          <div key={m.label} className="flex items-center gap-1 sm:gap-1.5 min-w-0">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full shrink-0" style={{ backgroundColor: m.color }} />
            <span className="text-[10px] sm:text-xs text-gray-500 truncate">{m.label} {(values[i] * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}
